import {inject, signal} from '@angular/core';
import {catchError, EMPTY, Observable} from 'rxjs';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {Keys} from '@shared/keys';

/**
 * Abstract base class for entity details modal components (assets, investments, accounts).
 *
 * Encapsulates the shared table state management, sorting, searching, pagination,
 * and transaction loading lifecycle that is identical across all entity details modals.
 *
 * Subclasses provide: column configuration, search fields, sorted-table enum values,
 * transaction loading logic, and an optional pre-load validation check.
 *
 * @typeParam TEntity - The entity view model type (e.g. EntityAssetViewModel)
 * @typeParam TTransaction - The transaction view model type (e.g. EntityAssetTransactionViewModel)
 */
export abstract class BaseEntityDetailsModal<TEntity extends DataViewModel, TTransaction extends DataViewModel> {

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);

  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('date');
  readonly sortOrder = signal(-1);
  readonly currentPage = signal<PageViewModel<TTransaction>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private searchString = '';
  private pageNumber = 1;
  private readonly pageSize = 20;

  protected readonly sessionStorageService = inject(SessionStorageService);
  protected readonly clientSideDataService = inject<ClientsideDataService<TTransaction>>(ClientsideDataService);

  /** Returns the column configuration for this modal's transaction table. */
  protected abstract populateColumns(): TableTemplateColumnConfig[];

  /** Returns the fields to search within for the transaction table. */
  protected abstract getSearchFields(): Keys<TTransaction>;

  /** Returns the SortedTable enum value used to restore the stored sort on init (e.g. SortedTable.Assets). */
  protected abstract getSortedTable(): SortedTable;

  /** Returns the SortedTable enum value used to persist sort changes for transactions (e.g. SortedTable.AssetTransactions). */
  protected abstract getTransactionSortedTable(): SortedTable;

  /**
   * Loads transactions from the backend and converts them to view models.
   * Called during initialization after validation passes.
   * @param entity The entity whose transactions to load
   * @returns Observable of converted transaction view models
   */
  protected abstract loadTransactions(entity: TEntity): Observable<TTransaction[]>;

  /**
   * Determines whether the transaction load should be skipped (e.g. missing required fields).
   * When true, the modal shows the empty state without making a service call.
   * Default implementation returns false (always load).
   */
  protected shouldSkipLoad(_entity: TEntity): boolean {
    return false;
  }

  /**
   * Shared initialization logic for all entity details modals.
   * Call from each subclass ngOnInit after setting the entity-specific property.
   *
   * @param entity The entity view model from the modal input
   * @param logPrefix A prefix for console error messages (e.g. 'EntityAssetDetailsModal')
   */
  protected initializeModal(entity: TEntity, logPrefix: string): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.columns.set(this.populateColumns());
    const storedSort = this.sessionStorageService.getSort(this.getSortedTable());
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    if (this.shouldSkipLoad(entity)) {
      this.dataLoading.set(false);
      this.dataEmpty.set(true);
      this.dataAvailable.set(false);
      return;
    }

    this.loadTransactions(entity).pipe(
      catchError(error => {
        console.error(`${logPrefix}: failed to load transactions`, error);
        this.dataLoading.set(false);
        this.dataEmpty.set(true);
        this.dataAvailable.set(false);
        return EMPTY;
      })
    ).subscribe(transactionVMs => {
      this.clientSideDataService.initialize(transactionVMs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(transactionVMs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber = 1;
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString = searchTerm;
    this.pageNumber = 1;
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber = targetPage;
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.update(v => v * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(this.getTransactionSortedTable(), this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.getSearchFields(),
      this.searchString,
      this.pageNumber,
      this.pageSize
    ));
  }
}
